import { useEffect, type SyntheticEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BRAND } from '@/brand';
import { brandingApi, logoUrl } from '@/api/branding';

export const BRANDING_QUERY_KEY = 'branding';

/**
 * The tenant's white-label logo (Settings > Branding), or the build-time
 * brand mark when it has none. null while that is still unknown, so a
 * customer's sign-in page never flashes the default mark first; callers keep
 * the space. The favicon follows the logo.
 */
export function useTenantLogo(tenantSlug: string | null): string | null {
  const { data, isPending } = useQuery({
    queryKey: [BRANDING_QUERY_KEY, tenantSlug ?? ''],
    queryFn: () => brandingApi.get(tenantSlug),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const src = isPending ? null : data?.has_logo ? logoUrl(tenantSlug, data.updated_at) : BRAND.mark;

  useEffect(() => {
    if (!src) return;
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (link) link.href = src === BRAND.mark ? BRAND.favicon : src;
  }, [src]);

  return src;
}

/** onError for a logo <img>: fall back to the brand mark once. */
export function fallBackToBrandMark(e: SyntheticEvent<HTMLImageElement>) {
  const img = e.currentTarget;
  if (!img.src.endsWith(BRAND.mark)) img.src = BRAND.mark;
}
